import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Navigate } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Flex,
  Heading,
  Loader,
  LockIcon,
  Text,
} from '@buffer-mono/popcorn'

import {
  useCanPostOnAnyChannel,
  useChannels,
} from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'

import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import {
  clearSession,
  loadSession,
} from './components/MigrationWizard/importSession'
import { MigrationWizard } from './components/MigrationWizard/MigrationWizard'
import { SOURCE_LABELS } from './components/MigrationWizard/types'
import type {
  ImportSession,
  MigrationSource,
} from './components/MigrationWizard/types'

import styles from './Import.module.css'

const MIGRATION_SOURCES: Array<{
  id: MigrationSource
  label: string
  description: string
}> = [
  {
    id: 'hootsuite',
    label: 'Hootsuite',
    description:
      'Import your scheduled, draft, and published posts from a Hootsuite CSV export.',
  },
]

const Import = (): JSX.Element => {
  const { isEnabled, isReady, hasClient } = useSplitEnabled(
    'migration-assistant',
  )
  const { loading: channelsLoading } = useChannels()
  const canPostOnAnyChannel = useCanPostOnAnyChannel()
  const [activeSource, setActiveSource] = useState<MigrationSource | null>(null)
  const [resumeSession, setResumeSession] = useState<ImportSession | null>(null)
  const [savedSession, setSavedSession] = useState<ImportSession | null>(null)

  useEffect(() => {
    const session = loadSession()
    setSavedSession(session)
  }, [])

  const handleResume = useCallback(() => {
    if (savedSession) {
      setResumeSession(savedSession)
      setActiveSource(savedSession.source)
    }
  }, [savedSession])

  const handleDismissSession = useCallback(() => {
    clearSession()
    setSavedSession(null)
  }, [])

  const handleClose = useCallback(() => {
    setActiveSource(null)
    setResumeSession(null)
    setSavedSession(loadSession())
  }, [])

  const completedCount = savedSession
    ? savedSession.items.filter((i) => i.status === 'success').length
    : 0
  const totalCount = savedSession?.items.length ?? 0

  if (!isReady || !hasClient || channelsLoading) return <Loader />
  if (!isEnabled) return <Navigate to="/settings" replace />

  if (!canPostOnAnyChannel) {
    return (
      <NewSettingsPageLayout title="Import Content">
        <EmptyState>
          <EmptyState.Icon>
            <LockIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Contact an admin to import content
          </EmptyState.Heading>
          <EmptyState.Description>
            An import creates posts on your channels, and you have view-only
            access to them.
          </EmptyState.Description>
        </EmptyState>
      </NewSettingsPageLayout>
    )
  }

  return (
    <NewSettingsPageLayout
      title="Import Content"
      subtitle="Migrate your content from other social media management tools"
      wide
    >
      <Flex direction="column" gap="lg">
        {savedSession && !activeSource && (
          <Banner variant="info">
            <Banner.Text>
              You have an unfinished {SOURCE_LABELS[savedSession.source]} import
              ({completedCount} of {totalCount} posts completed). Resume where
              you left off?
            </Banner.Text>
            <Flex gap="xs">
              <Button size="small" variant="primary" onClick={handleResume}>
                Resume
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={handleDismissSession}
              >
                Discard
              </Button>
            </Flex>
          </Banner>
        )}

        <Flex direction="column" gap="sm">
          <Heading size="small">Where are you migrating from?</Heading>
          <Text color="subtle">
            Select the platform you&apos;d like to import content from. You can
            import content from multiple platforms.
          </Text>
        </Flex>

        <Flex gap="md" wrap="wrap">
          {MIGRATION_SOURCES.map((source) => (
            <Card key={source.id} className={styles.sourceCard}>
              <Flex direction="column" gap="sm" fullWidth>
                <Heading size="small">{source.label}</Heading>
                <Text color="subtle" size="sm">
                  {source.description}
                </Text>
                <Button
                  variant="secondary"
                  onClick={() => setActiveSource(source.id)}
                >
                  Import from {source.label}
                </Button>
              </Flex>
            </Card>
          ))}
        </Flex>
      </Flex>

      {activeSource && (
        <MigrationWizard
          source={activeSource}
          resumeSession={resumeSession}
          onClose={handleClose}
        />
      )}
    </NewSettingsPageLayout>
  )
}

export default Import
